import React, { useState, useEffect } from 'react';
import chemicalService from '../../../services/chemicalService';

const PAYMENT_MODES = ['cash', 'bank_transfer', 'cheque', 'easypaisa', 'jazzcash'];

const ExpenseFormModal = ({ expense, onClose, onSaved }) => {
  const isEdit = Boolean(expense);

  const [expenseTypes, setExpenseTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);

  const [form, setForm] = useState({
    expense_date: new Date().toISOString().split('T')[0],
    expense_type_id: '',
    amount: '',
    payment_mode: 'cash',
    paid_to: '',
    description: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Load expense types
  useEffect(() => {
    const load = async () => {
      try {
        setLoadingTypes(true);
        const res = await chemicalService.getExpenseTypes({ page: 1, limit: 500, is_active: true });
        setExpenseTypes(res.data || []);
      } catch (err) {
        console.error('Error loading expense types:', err);
        alert('❌ Failed to load expense types');
      } finally {
        setLoadingTypes(false);
      }
    };
    load();
  }, []);

  // Hydrate when editing
  useEffect(() => {
    if (expense) {
      setForm({
        expense_date: expense.expense_date ? expense.expense_date.split('T')[0] : new Date().toISOString().split('T')[0],
        expense_type_id: expense.expense_type_id || '',
        amount: expense.amount != null ? expense.amount : '',
        payment_mode: expense.payment_mode || 'cash',
        paid_to: expense.paid_to || '',
        description: expense.description || '',
      });
    }
  }, [expense]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.expense_date) return alert('❌ Date is required.');
    if (!form.expense_type_id) return alert('❌ Select an expense type.');
    if (!form.amount || parseFloat(form.amount) <= 0) return alert('❌ Amount must be > 0.');
    if (!form.payment_mode) return alert('❌ Payment mode is required.');

    try {
      setSubmitting(true);
      const payload = {
        expense_date: form.expense_date,
        expense_type_id: parseInt(form.expense_type_id, 10),
        amount: parseFloat(form.amount),
        payment_mode: form.payment_mode,
        paid_to: form.paid_to.trim() || null,
        description: form.description.trim() || null,
      };

      if (isEdit) {
        await chemicalService.updateExpense(expense.id, payload);
        alert('✅ Expense updated successfully!');
      } else {
        await chemicalService.createExpense(payload);
        alert('✅ Expense recorded successfully!');
      }
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save expense';
      alert('❌ ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedType = expenseTypes.find((t) => t.id === parseInt(form.expense_type_id, 10));

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>
              {isEdit ? '✏️ Edit Expense' : '💸 New Expense'}
            </h2>
            <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '4px 0 0 0' }}>
              {isEdit ? 'Updating expense entry' : 'Record an operational expense'}
            </p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px 30px' }}>

            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Date <span style={styles.required}>*</span>
                </label>
                <input
                  type="date"
                  value={form.expense_date}
                  onChange={(e) => handleChange('expense_date', e.target.value)}
                  style={styles.input}
                  required
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div style={{ ...styles.formGroup, flex: 2 }}>
                <label style={styles.label}>
                  Expense Type <span style={styles.required}>*</span>
                </label>
                <select
                  value={form.expense_type_id}
                  onChange={(e) => handleChange('expense_type_id', e.target.value)}
                  style={styles.input}
                  required
                  disabled={loadingTypes}
                >
                  <option value="">
                    {loadingTypes
                      ? 'Loading types...'
                      : expenseTypes.length === 0
                        ? '-- No active expense types. Add some in Settings tab. --'
                        : '-- Select type --'}
                  </option>
                  {expenseTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
                {selectedType && (
                  <div style={{ marginTop: '4px' }}>
                    <span style={{
                      ...styles.typePreview,
                      background: (selectedType.color || '#7f8c8d') + '20',
                      color: selectedType.color || '#7f8c8d',
                      border: `1px solid ${(selectedType.color || '#7f8c8d')}40`,
                    }}>
                      ● {selectedType.name}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Amount (Rs) <span style={styles.required}>*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  style={{ ...styles.input, fontSize: '18px', fontWeight: '700', color: '#e74c3c' }}
                  placeholder="0"
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Payment Mode <span style={styles.required}>*</span>
                </label>
                <select
                  value={form.payment_mode}
                  onChange={(e) => handleChange('payment_mode', e.target.value)}
                  style={styles.input}
                  required
                >
                  {PAYMENT_MODES.map((m) => (
                    <option key={m} value={m}>{formatPaymentMode(m)}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Paid To (optional)</label>
              <input
                type="text"
                value={form.paid_to}
                onChange={(e) => handleChange('paid_to', e.target.value)}
                style={styles.input}
                placeholder="e.g. K-Electric, SNGPL, Imran rider, ABC Supplies"
              />
              <span style={styles.hint}>
                Who/where the money went — for your records
              </span>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Description (optional)</label>
              <textarea
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                style={{ ...styles.input, minHeight: '70px', resize: 'vertical', fontFamily: 'inherit' }}
                placeholder="e.g. Electricity bill May 2026, Office cleaning supplies, etc."
                rows="3"
              />
            </div>

          </div>

          <div style={styles.modalFooter}>
            <button type="button" onClick={onClose} style={styles.cancelButton} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" style={styles.submitButton} disabled={submitting}>
              {submitting ? '⏳ Saving...' : (isEdit ? '💾 Update Expense' : '💾 Save Expense')}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

const formatPaymentMode = (m) => {
  const map = {
    cash: 'Cash',
    bank_transfer: 'Bank Transfer',
    cheque: 'Cheque',
    easypaisa: 'EasyPaisa',
    jazzcash: 'JazzCash',
  };
  return map[m] || m;
};

const styles = {
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
  },
  modal: {
    background: 'white', borderRadius: '12px',
    width: '95%', maxWidth: '640px', maxHeight: '92vh',
    overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    padding: '20px 30px', borderBottom: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
  },
  modalTitle: { fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 },
  closeButton: {
    padding: '8px 16px', background: '#f0f0f0', border: 'none',
    borderRadius: '8px', cursor: 'pointer',
    fontSize: '20px', fontWeight: '700', color: '#666',
  },
  row: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  formGroup: {
    flex: 1, minWidth: '180px',
    display: 'flex', flexDirection: 'column',
    gap: '6px', marginBottom: '14px',
  },
  label: { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  required: { color: '#e74c3c' },
  input: {
    width: '100%', padding: '10px 12px',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    fontSize: '14px', boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  hint: {
    fontSize: '11px', color: '#95a5a6', fontStyle: 'italic',
  },
  typePreview: {
    padding: '4px 12px', borderRadius: '10px',
    fontSize: '12px', fontWeight: '700',
    display: 'inline-block',
  },
  modalFooter: {
    padding: '16px 30px',
    borderTop: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'flex-end', gap: '12px',
  },
  cancelButton: {
    padding: '12px 24px',
    background: '#95a5a6', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
  submitButton: {
    padding: '12px 28px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
};

export default ExpenseFormModal;